import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Siren } from 'lucide-react';
import { getCrisis, CrisisIncident } from '../../services/dataCockpitApi';
import { usePeriode } from '../../store/dataPeriodeStore';
import { DataFilterBalk, DataKop, Kaart, KpiTegel, Laden, Fout } from '../../components/data/DataFilterBalk';
import { euro } from '../../components/data/DataCharts';

const Dekking: React.FC<{ i: CrisisIncident; tarief: number; minUren: number }> = ({ i, tarief, minUren }) => {
  const { t } = useTranslation('employer');
  if (i.dekking.type === 'VOLLEDIG') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">🟢 {t('dataCockpit.crisis.gedekt')}</span>;
  }
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900">🟡 {t('dataCockpit.crisis.onDemand')}</span>
      <span className="text-[11px] text-gray-500">{t('dataCockpit.crisis.onDemandRegel', { tarief: euro(tarief), min: minUren, gedekt: i.dekking.gedekt, gewenst: i.dekking.gewenst })}</span>
    </span>
  );
};

/** Submap 2 — Calamiteiten & Crisis-Claims (de Crisispool). */
export const DataCrisisPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const { periode, key } = usePeriode();
  const q = useQuery({ queryKey: ['data-crisis', ...key], queryFn: () => getCrisis(periode) });
  const d = q.data;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <DataKop titel={t('dataCockpit.crisis.titel')} ondertitel={t('dataCockpit.crisis.ondertitel')} icoon={<Siren className="h-6 w-6" />} />
      <DataFilterBalk />
      {q.isLoading && <Laden />}
      {q.isError && <Fout fout={q.error} />}
      {d && !d.fonds.module_actief && (
        <Kaart><p className="py-8 text-center text-sm text-gray-500">{t('dataCockpit.crisis.geenModule')}</p></Kaart>
      )}
      {d && d.fonds.module_actief && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiTegel label={t('dataCockpit.crisis.kpiIncidenten')} waarde={d.totalen.incidenten} sub={t('dataCockpit.crisis.kpiInzet', { single: d.totalen.single, dual: d.totalen.dual })} />
            <KpiTegel label={t('dataCockpit.crisis.kpiUren')} waarde={d.totalen.uren_gefactureerd.toLocaleString('nl-NL')} sub={t('dataCockpit.crisis.kpiResponders', { n: d.totalen.responders })} />
            <KpiTegel label={t('dataCockpit.crisis.kpiOnDemand')} waarde={euro(d.totalen.on_demand_bedrag, 2)} sub={t('dataCockpit.crisis.kpiOnDemandSub', { n: d.totalen.on_demand })} />
            <KpiTegel label={t('dataCockpit.crisis.kpiTegoed')} waarde={d.fonds.resterend} sub={t('dataCockpit.crisis.kpiTegoedSub', { gebruikt: d.fonds.gebruikte_uitrukken, vrij: d.fonds.vrije_uitrukken })} />
          </div>

          <Kaart titel={t('dataCockpit.crisis.logboek')} uitleg={t('dataCockpit.crisis.logboekUitleg')}>
            {d.logboek.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">{t('dataCockpit.crisis.geenIncidenten')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                      <th className="py-2 pr-4 font-medium">{t('dataCockpit.crisis.kolIncident')}</th>
                      <th className="py-2 pr-4 font-medium">{t('dataCockpit.crisis.kolDatum')}</th>
                      <th className="py-2 pr-4 font-medium">{t('dataCockpit.crisis.kolType')}</th>
                      <th className="py-2 pr-4 font-medium">{t('dataCockpit.crisis.kolUren')}</th>
                      <th className="py-2 pr-4 font-medium">{t('dataCockpit.crisis.kolGeo')}</th>
                      <th className="py-2 pr-4 font-medium">{t('dataCockpit.crisis.kolDekking')}</th>
                      <th className="py-2 text-right font-medium">{t('dataCockpit.crisis.kolKosten')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.logboek.map((i) => (
                      <tr key={i.id} className="border-b border-gray-100 align-top">
                        <td className="py-2.5 pr-4">
                          <p className="font-medium text-gray-900">🚨 {i.incident_label}</p>
                          <p className="text-xs text-gray-500">{[i.vestiging, i.vestiging_plaats].filter(Boolean).join(' · ') || '—'}</p>
                        </td>
                        <td className="py-2.5 pr-4 whitespace-nowrap">{new Date(i.incident_datum).toLocaleDateString('nl-NL')}<p className="text-xs text-gray-500">{i.dagdeel}</p></td>
                        <td className="py-2.5 pr-4 whitespace-nowrap">{i.inzet_type === 'DUAL_RESPONSE' ? `👥 ${t('dataCockpit.crisis.dual')}` : `👤 ${t('dataCockpit.crisis.single')}`}</td>
                        <td className="py-2.5 pr-4 whitespace-nowrap">
                          {i.uren_gefactureerd.toLocaleString('nl-NL')} u
                          {!i.uren_definitief && <p className="text-xs text-gray-500">{t('dataCockpit.crisis.prognose')}</p>}
                        </td>
                        <td className="py-2.5 pr-4 text-xs">
                          {i.reis_overloop ? (
                            <>
                              <p className="font-medium text-gray-800">{i.escalatie_label}</p>
                              <p className="text-gray-500">{t('dataCockpit.crisis.geoRegel', { km: i.reis_km_totaal.toLocaleString('nl-NL'), uren: i.reistijd_forfait_uren.toLocaleString('nl-NL'), kosten: euro(i.reiskosten, 2) })}</p>
                            </>
                          ) : (
                            <span className="text-gray-400">{t('dataCockpit.crisis.eigenRegio')}</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4"><Dekking i={i} tarief={d.on_demand_uurtarief} minUren={d.min_uren_pp} /></td>
                        <td className="py-2.5 text-right whitespace-nowrap">
                          <p className="font-semibold text-gray-900">{euro(i.factuur_bedrag, 2)}</p>
                          <p className="text-xs text-gray-500">{t('dataCockpit.crisis.totaalInzet', { bedrag: euro(i.kosten_totaal, 2) })}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-3 text-xs text-gray-400">{t('dataCockpit.crisis.geoUitleg')}</p>
              </div>
            )}
          </Kaart>
        </div>
      )}
    </div>
  );
};

export default DataCrisisPage;
